// Oct 31 2026, user's ask after order 100162725C skipped 100162725AL1: "create
// an admin assignment - if item have active [BOM] but physically its purchase
// by the vendor & user required store request, admin just assign that item &
// store request create same as other's item". Per item AND site, the user's
// choice - the same part can be bought at one plant and made at another.
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Storefront, Trash, CircleNotch, CaretUpDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ItemSearch = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleQueryChange = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q: value.trim(), limit: 15 } });
        setResults(data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 w-full flex items-center justify-between px-2 rounded-sm border border-[#D0D5DD] bg-white text-[13px] text-left"
          data-testid="store-issued-item-search-trigger"
        >
          <span className="text-[#98A2B3] flex items-center gap-2"><MagnifyingGlass size={13} /> Search item ID or description...</span>
          <CaretUpDown size={12} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={handleQueryChange} placeholder="Type at least 1 character..." data-testid="store-issued-item-search-input" />
          <CommandList>
            {loading && <div className="py-3 text-center text-xs text-[#667085]">Searching...</div>}
            {!loading && query.trim() && results.length === 0 && <CommandEmpty>No matching item found.</CommandEmpty>}
            <CommandGroup>
              {results.map((r) => (
                <CommandItem
                  key={r.product_id}
                  value={r.product_id}
                  onSelect={() => { onSelect(r); setOpen(false); setQuery(""); setResults([]); }}
                  data-testid={`store-issued-item-option-${r.product_id}`}
                >
                  <span className="font-mono text-xs font-bold text-[#101828] mr-2">{r.product_id}</span>
                  <span className="text-xs text-[#667085]">{r.description || "—"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const StoreIssuedItemsCard = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [site, setSite] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingKey, setRemovingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/store-issued-items`);
      setItems(data.items || []);
      setSites(data.sites || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load store-issued items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const assign = async () => {
    if (!selected || !site) { toast.error("Pick an item and a site first"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/store-issued-items`, { product_id: selected.product_id, site_id: site, description: selected.description });
      toast.success(`${selected.product_id} will now get a store request at ${site}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign item");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    const key = `${row.product_id}::${row.site_id}`;
    setRemovingKey(key);
    try {
      await axios.delete(`${API}/store-issued-items/${encodeURIComponent(row.product_id)}/${encodeURIComponent(row.site_id)}`);
      toast.success(`${row.product_id} removed from ${row.site_id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove assignment");
    } finally {
      setRemovingKey(null);
    }
  };

  return (
    <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 mb-3" data-testid="store-issued-items-card">
      <div className="flex items-center gap-2 mb-1">
        <Storefront size={15} weight="fill" className="text-[#0E7C86]" />
        <h2 className="font-heading text-[14px] font-bold text-[#101828]">Store-Issued Items</h2>
      </div>
      <p className="text-xs text-[#475467] mb-3 max-w-3xl">
        An item that has its own BOM in SAP is treated as an in-house sub-assembly and never reaches a store request.
        Assign it here when it is actually bought from a vendor at that plant - it then counts as a shortage and waits
        for Store Approval exactly like any other purchased component.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="w-[320px]">
          <Label className="text-[11px] font-bold text-[#344054] mb-1 block">Item</Label>
          {selected ? (
            <div className="h-8 flex items-center px-2 rounded-sm border border-[#0E7C86] bg-[#ECFDF3] text-[13px]" data-testid="store-issued-item-selected">
              <span className="font-mono font-bold text-[#101828] mr-2">{selected.product_id}</span>
              <span className="text-[#667085] truncate">{selected.description}</span>
              <button onClick={() => setSelected(null)} className="ml-auto text-[#667085] hover:text-[#B42318] pl-2" data-testid="store-issued-item-clear">✕</button>
            </div>
          ) : (
            <ItemSearch onSelect={setSelected} />
          )}
        </div>
        <div className="w-[130px]">
          <Label className="text-[11px] font-bold text-[#344054] mb-1 block">Site</Label>
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="h-8 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="store-issued-site-select">
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s} value={s} className="text-[13px]" data-testid={`store-issued-site-option-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={assign}
          disabled={!selected || !site || saving}
          className="h-8 bg-[#0E7C86] hover:bg-[#0B6B74] text-xs rounded-sm"
          data-testid="store-issued-assign-btn"
        >
          {saving ? <CircleNotch size={13} className="animate-spin" /> : "Assign"}
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-[#667085]" data-testid="store-issued-loading">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-[#667085]" data-testid="store-issued-empty">No item assigned yet.</p>
      ) : (
        <div className="border border-[#D0D5DD] rounded-sm overflow-auto">
          <table className="w-full text-[13px] border-collapse" data-testid="store-issued-table">
            <thead>
              <tr>
                {["Item", "Description", "Site", "Assigned By", ""].map((h) => (
                  <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={`${row.product_id}::${row.site_id}`} data-testid={`store-issued-row-${row.product_id}-${row.site_id}`}>
                  <td className="border border-[#D0D5DD] p-1.5 font-mono font-bold text-[#101828]">{row.product_id}</td>
                  <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#475467]">{row.description || "—"}</td>
                  <td className="border border-[#D0D5DD] p-1.5 text-xs font-bold text-[#0E7C86]">{row.site_id}</td>
                  <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#475467]">{row.assigned_by || "—"}</td>
                  <td className="border border-[#D0D5DD] p-1.5 w-10">
                    <button
                      type="button"
                      onClick={() => remove(row)}
                      disabled={removingKey === `${row.product_id}::${row.site_id}`}
                      className="text-[#B42318] disabled:opacity-40"
                      data-testid={`store-issued-remove-${row.product_id}-${row.site_id}`}
                    >
                      {removingKey === `${row.product_id}::${row.site_id}` ? <CircleNotch size={13} className="animate-spin" /> : <Trash size={13} weight="bold" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
